/**
 * Plan catalogue — the single source of truth for plan display metadata shown
 * on the billing page (names, taglines, badges, feature lists). Numeric limits
 * are kept ALIGNED with `PLAN_TIERS` in
 * `packages/domain/src/subscription-billing/compute-entitlements.ts` — change
 * both together. Pricing is intentionally absent (V1 activation is sales-led).
 */

export type PlanCode = "starter" | "growth" | "scale" | "design_partner";
export type PlanBadgeTone = "popular" | "exclusive";

export interface PlanCatalogEntry {
  code: PlanCode;
  name: string;
  tagline: string;
  /** Limits — must match PLAN_TIERS. */
  seats: number;
  storageGb: number;
  waCreditsPerMonth: number;
  badge: string | null;
  badgeTone: PlanBadgeTone | null;
  /** "What's included" lines (current-plan card). */
  features: string[];
  /**
   * Monthly price in integer paise, GST-inclusive — display only. Razorpay's
   * Plan (created by `scripts/razorpay/setup-plans.ts`) is the authority for
   * what's actually charged; keep this MIRRORED with those amounts. `null` =
   * not self-serve (Design Partner is provisioned by the team), so no
   * "Activate" / checkout CTA is offered. @see ADR-0023 §3.
   */
  pricePaiseMonthly: number | null;
}

export const PLAN_CATALOG: readonly PlanCatalogEntry[] = [
  {
    code: "starter",
    name: "Starter",
    tagline: "For founders getting off the ground",
    seats: 3,
    storageGb: 5,
    waCreditsPerMonth: 2_000,
    badge: null,
    badgeTone: null,
    pricePaiseMonthly: 149_900, // ₹1,499/mo — mirrors setup-plans.ts
    features: [
      "Invoices, credit notes & receipts",
      "Purchase orders, GRNs & vendor bills",
      "Cash flow — bank, petty cash, transfers",
      "GST returns & TDS compliance",
      "Document templates & PDF export",
      "Audit log",
    ],
  },
  {
    code: "growth",
    name: "Growth",
    tagline: "For growing teams that need control",
    seats: 10,
    storageGb: 20,
    waCreditsPerMonth: 8_000,
    badge: "Most popular",
    badgeTone: "popular",
    pricePaiseMonthly: 399_900, // ₹3,999/mo — mirrors setup-plans.ts
    features: [
      "Everything in Starter",
      "Custom roles & permissions",
      "Advanced reporting",
      "AI assistant + document knowledge base",
      "Priority WhatsApp & email delivery",
    ],
  },
  {
    code: "scale",
    name: "Scale",
    tagline: "For larger teams running at volume",
    seats: 25,
    storageGb: 100,
    waCreditsPerMonth: 25_000,
    badge: null,
    badgeTone: null,
    pricePaiseMonthly: 899_900, // ₹8,999/mo — mirrors setup-plans.ts
    features: [
      "Everything in Growth",
      "Up to 25 team members",
      "100 GB document storage",
      "25,000 WhatsApp credits / month",
      "Priority onboarding & support",
    ],
  },
  {
    code: "design_partner",
    name: "Design Partner",
    tagline: "A direct line to the product team",
    seats: 25,
    storageGb: 100,
    waCreditsPerMonth: 25_000,
    badge: "Exclusive",
    badgeTone: "exclusive",
    pricePaiseMonthly: null, // provisioned by the team — not self-serve
    features: [
      "Everything in Scale",
      "Weekly calls with the product team",
      "Priority support — same-day response",
      "Early access to V1.x features",
      "Co-design upcoming modules",
    ],
  },
] as const;

export const PLAN_BY_CODE: Record<string, PlanCatalogEntry> = Object.fromEntries(
  PLAN_CATALOG.map((p) => [p.code, p]),
);

/**
 * Format a monthly plan price for display, e.g. `₹3,999/mo`. Returns null for
 * non-self-serve plans (Design Partner), so callers can hide the price + the
 * "Activate" CTA. Whole rupees — our plan prices have no paise component.
 */
export function formatPlanPrice(entry: PlanCatalogEntry): string | null {
  if (entry.pricePaiseMonthly == null) return null;
  return `₹${(entry.pricePaiseMonthly / 100).toLocaleString("en-IN")}/mo`;
}

/** True when a plan can be activated/charged self-serve (has a price + Razorpay plan). */
export function isSelfServePlan(entry: PlanCatalogEntry): boolean {
  return entry.pricePaiseMonthly != null;
}

/** The next tier up from `code` (for the "room to grow" upsell), or null at the top. */
export function nextPlan(code: string): PlanCatalogEntry | null {
  const i = PLAN_CATALOG.findIndex((p) => p.code === code);
  if (i < 0 || i >= PLAN_CATALOG.length - 1) return null;
  return PLAN_CATALOG[i + 1] ?? null;
}

/**
 * Prepaid WhatsApp credit top-up packs (system-decisions §16: "top-up beyond
 * bundle — prepaid via Razorpay one-time payment").
 *
 * Priced above the same margin floor as AI (credit-pricing.ts): a WhatsApp
 * credit's worst-case cost-to-serve is ≤ ₹0.25 (the shared unit — marketing is 4
 * credits for ₹0.86, intl-auth 10 for ₹2.30, both ≤ ₹0.25/credit), so the ~40
 * paise/credit floor applies here too. These packs run 42–50 paise/credit and
 * are verified by the credit-pricing guardrail test. (The old ₹0.35/credit 10k
 * pack was below the floor — a loss on marketing-heavy usage.)
 */
/** A prepaid credit pack, for either wallet (centralized credit subsystem, ADR-0033). */
export interface TopupPack {
  /** Stable id used as the Razorpay order note + UI key. Unique across wallets. */
  id: string;
  /** Which wallet the purchased credits land in. */
  wallet: "whatsapp" | "ai";
  credits: number;
  /** Price in integer paise (GST-inclusive). PLACEHOLDER — see note above. */
  pricePaise: number;
  /** Optional badge, e.g. "Best value". */
  badge?: string;
}

/** @deprecated use TopupPack — kept for back-compat. */
export type WaTopupPack = TopupPack;

export const WA_TOPUP_PACKS: readonly TopupPack[] = [
  { id: "wa_1k", wallet: "whatsapp", credits: 1_000, pricePaise: 500_00 }, // ₹0.50/cr
  { id: "wa_5k", wallet: "whatsapp", credits: 5_000, pricePaise: 2_200_00, badge: "Popular" }, // ₹0.44/cr
  { id: "wa_10k", wallet: "whatsapp", credits: 10_000, pricePaise: 4_200_00, badge: "Best value" }, // ₹0.42/cr
] as const;

/**
 * Prepaid AI credit top-up packs (ADR-0033 — centralized credit subsystem). For
 * when the monthly AI allotment runs dry.
 *
 * Prices are set ABOVE the margin floor from `credit-pricing.ts`: 1 AI credit
 * costs ₹0.25 to serve (LLM + OpenRouter), and after 18% GST + ~2% gateway the
 * floor that still keeps ≥20% net margin is ~40 paise/credit. These packs run
 * 42–60 paise/credit (volume discount down the list), so every pack clears the
 * floor with buffer — verified by the credit-pricing guardrail test. Re-derive
 * if the FX/vendor anchors in model-pricing.ts move.
 */
export const AI_TOPUP_PACKS: readonly TopupPack[] = [
  { id: "ai_500", wallet: "ai", credits: 500, pricePaise: 299_00 }, // ₹0.598/cr · ~41% margin
  { id: "ai_2k", wallet: "ai", credits: 2_000, pricePaise: 999_00, badge: "Popular" }, // ₹0.50/cr · ~33% margin
  { id: "ai_10k", wallet: "ai", credits: 10_000, pricePaise: 4_199_00, badge: "Best value" }, // ₹0.42/cr · ~23% margin
] as const;

export const WA_TOPUP_PACK_BY_ID: Record<string, TopupPack> = Object.fromEntries(
  WA_TOPUP_PACKS.map((p) => [p.id, p]),
);

/** Every top-up pack (both wallets), keyed by id — the order procedure looks up here. */
export const TOPUP_PACK_BY_ID: Record<string, TopupPack> = Object.fromEntries(
  [...WA_TOPUP_PACKS, ...AI_TOPUP_PACKS].map((p) => [p.id, p]),
);
